import type { AIFindingCategory, AIFindingSeverity } from "./ai-review-model.js";

/**
 * REGRAS TÉCNICAS DETERMINÍSTICAS — terminologia, coerência, normas e
 * unidades. Nenhuma delas é aplicada automaticamente: todas produzem
 * ALERTA com o motivo, e a decisão é da pessoa.
 */

export interface TechnicalFinding {
  readonly ruleId: string;
  readonly category: AIFindingCategory;
  readonly severity: AIFindingSeverity;
  readonly fieldPath: string;
  readonly originalText: string;
  readonly suggestedText: string;
  readonly explanation: string;
  readonly justifiable: boolean;
}

export interface ReviewField {
  readonly fieldPath: string;
  readonly label: string;
  readonly text: string;
  /** Campo de conclusão/observação final — é contra ele que a coerência é conferida. */
  readonly conclusive?: boolean;
  /** Pode receber texto aplicado pela revisão (falso = só sugestão; edição no módulo). */
  readonly writable?: boolean;
}

/** Fatos estruturados do relatório — os números NUNCA vêm do texto livre. */
export interface ReviewFacts {
  /** Espessuras (ou equivalentes) com o mínimo aplicável, em mm. */
  readonly measurements: ReadonlyArray<{ readonly label: string; readonly value: number | null; readonly min: number | null }>;
  /** Resultado de cada item/componente. */
  readonly results: ReadonlyArray<{ readonly label: string; readonly verdict: "APPROVED" | "REJECTED" | "PENDING" | "OTHER" }>;
  readonly recommendationsCount: number | null;
  /** Normas/procedimentos declarados no relatório (referência, critério, procedimento). */
  readonly declaredReferences: readonly string[];
  /** Datas do relatório (rótulo → AAAA-MM-DD) para a validação determinística. */
  readonly dates: ReadonlyArray<{ readonly label: string; readonly value: string }>;
  readonly periods: ReadonlyArray<{ readonly label: string; readonly start: string; readonly end: string }>;
}

export const EMPTY_FACTS: ReviewFacts = { measurements: [], results: [], recommendationsCount: null, declaredReferences: [], dates: [], periods: [] };

// ═══════════════════════════════════════════════════════════ terminologia

/**
 * Termos suspeitos: o texto parece erro de termo técnico, mas trocar sozinho
 * poderia mudar o sentido ("corrosão por festa" quase certamente é "fresta",
 * mas quem decide é quem escreveu).
 */
const TERMINOLOGY: ReadonlyArray<{ pattern: RegExp; suggestion: (m: string) => string; note: string }> = [
  { pattern: /corros[ãa]o\s+por\s+festas?\b/gi, suggestion: m => m.replace(/festa/i, "fresta"), note: "Possível erro terminológico: \"corrosão por fresta\"?" },
  { pattern: /\bfatiga\b/gi, suggestion: () => "fadiga", note: "Possível erro terminológico: \"fadiga\"?" },
  { pattern: /\bpitting\b/gi, suggestion: () => "pite", note: "Termo em inglês: o termo técnico em português é \"pite\" (ou \"corrosão por pite\")." },
  { pattern: /\bsolda\s+de\s+fil[ée]\b/gi, suggestion: m => m.replace(/fil[ée]/i, "filete"), note: "Possível erro terminológico: \"solda de filete\"?" },
  { pattern: /\bcorros[ãa]o\s+sob\s+isola[çc][ãa]o\b/gi, suggestion: m => m.replace(/isola[çc][ãa]o/i, "isolamento"), note: "Termo consagrado: \"corrosão sob isolamento\" (CUI)." },
  { pattern: /\btrinca\s+por\s+corros[ãa]o\s+sob\s+tens[ãa]o\s+fraturante\b/gi, suggestion: m => m.replace(/\s+fraturante/i, ""), note: "Possível redundância terminológica." }
];

export function terminologyFindings(field: ReviewField): TechnicalFinding[] {
  const out: TechnicalFinding[] = [];
  for (const t of TERMINOLOGY) {
    for (const m of field.text.matchAll(t.pattern)) {
      out.push({
        ruleId: "TERM_SUSPECT", category: "TERMINOLOGIA", severity: "TERMINOLOGICO", fieldPath: field.fieldPath,
        originalText: m[0], suggestedText: t.suggestion(m[0]), explanation: t.note, justifiable: false
      });
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════ coerência

const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * Frases que afirmam condição boa — contraditórias com espessura abaixo do
 * mínimo ou item reprovado. "conforme" sozinho NÃO entra: aparece em frases
 * neutras ("conforme procedimento").
 */
const POSITIVE = /\b(sem\s+restric(ao|oes)|sem\s+anomalias?|aprovad[oa]s?|apto|liberad[oa]|dentro\s+dos?\s+limites?|em\s+conformidade|sem\s+indicac(ao|oes)|nenhuma\s+(anomalia|indicacao|restricao)|integro|integridade\s+preservada)\b/;
const ALL_APPROVED = /\b(todos?\s+(os\s+)?(pontos|itens|componentes|juntas|elementos)?\s*(foram\s+)?aprovad[oa]s|todos\s+aprovados|nenhuma\s+indicacao|sem\s+indicac(ao|oes)|nenhuma\s+(anomalia|descontinuidade))\b/;
/** Negação logo antes ("não apresenta restrições" é o contrário de "sem restrições"). */
const hasNegatedPositive = (t: string) => /\b(nao|sem)\s+(esta|estao|se\s+encontra)?\s*(aprovad|apto|conforme|liberad)/.test(t);
const NO_RECOMMENDATION = /\b(sem\s+recomendac(ao|oes)|nenhuma\s+recomendacao|nao\s+(ha|foram\s+geradas?|existem)\s+recomendac(ao|oes))\b/;

export function coherenceFindings(fields: readonly ReviewField[], facts: ReviewFacts): TechnicalFinding[] {
  const out: TechnicalFinding[] = [];
  const conclusive = fields.filter(f => f.conclusive && f.text.trim());
  const below = facts.measurements.filter(m => typeof m.value === "number" && typeof m.min === "number" && m.value < m.min);
  const rejected = facts.results.filter(r => r.verdict === "REJECTED");

  for (const field of conclusive) {
    const t = norm(field.text);
    const positive = POSITIVE.test(t) && !hasNegatedPositive(t);
    if (below.length > 0 && positive) {
      const m = below[0]!;
      out.push({
        ruleId: "COHERENCE_THICKNESS_BELOW_MIN", category: "RESULTADOS_CONCLUSAO", severity: "CRITICO", fieldPath: field.fieldPath,
        originalText: field.text, suggestedText: "",
        explanation: `A conclusão não está coerente com a espessura mínima informada: ${m.label} = ${fmt(m.value!)} mm, abaixo da mínima de ${fmt(m.min!)} mm${below.length > 1 ? ` (e mais ${below.length - 1} ponto(s))` : ""}.`,
        justifiable: false
      });
    } else if (rejected.length > 0 && (ALL_APPROVED.test(t) || positive)) {
      out.push({
        ruleId: "COHERENCE_REJECTED_VS_CONCLUSION", category: "RESULTADOS_CONCLUSAO", severity: "TECNICO", fieldPath: field.fieldPath,
        originalText: field.text, suggestedText: "",
        explanation: `A conclusão afirma aprovação, mas há resultado REPROVADO: ${rejected.slice(0, 3).map(r => r.label).join(", ")}${rejected.length > 3 ? "…" : ""}.`,
        justifiable: true
      });
    }
    if ((facts.recommendationsCount ?? 0) > 0 && NO_RECOMMENDATION.test(t)) {
      out.push({
        ruleId: "COHERENCE_RECOMMENDATIONS", category: "COERENCIA", severity: "TECNICO", fieldPath: field.fieldPath,
        originalText: field.text, suggestedText: "",
        explanation: `O texto diz que não há recomendações, mas o relatório tem ${facts.recommendationsCount}.`,
        justifiable: true
      });
    }
  }
  return out;
}

const fmt = (n: number) => n.toFixed(2).replace(".", ",");

// ═══════════════════════════════════════════════════════════ normas e unidades

const NORM_MENTION = /\b(API\s?\d{3}|ASME\s+(?:B\d+(?:\.\d+)?|VIII|PCC-\d)|NR-?\s?\d+|N-\d{3,4}|ABNT\s+NBR\s+\d+)\b/gi;
const compact = (s: string) => norm(s).replace(/[^a-z0-9.]/g, "");

/** Norma citada no texto que não está entre as referências do relatório nem na Base Técnica. */
export function normFindings(field: ReviewField, facts: ReviewFacts, knowledgeDocs: readonly string[]): TechnicalFinding[] {
  const declared = [...facts.declaredReferences, ...knowledgeDocs].map(compact);
  const out: TechnicalFinding[] = [];
  const seen = new Set<string>();
  for (const m of field.text.matchAll(NORM_MENTION)) {
    const key = compact(m[0]);
    if (seen.has(key)) continue;
    seen.add(key);
    if (declared.some(d => d.includes(key) || key.includes(d) && d.length > 3)) continue;
    out.push({
      ruleId: "NORM_NOT_DECLARED", category: "NORMAS", severity: "INFO", fieldPath: field.fieldPath,
      originalText: m[0], suggestedText: "",
      explanation: `A norma ${m[0]} é citada no texto, mas não consta nas referências do relatório nem na Base Técnica configurada. Confirme a citação.`,
      justifiable: false
    });
  }
  return out;
}

/** Unidade escrita fora do padrão (decimal com ponto, "MM"). Só sugere — o número nunca é tocado. */
export function unitFindings(field: ReviewField): TechnicalFinding[] {
  const out: TechnicalFinding[] = [];
  for (const m of field.text.matchAll(/\b\d+\.\d+\s?(mm|MM|kgf\/cm²|bar|°C)\b/g)) {
    out.push({
      ruleId: "UNIT_DECIMAL_POINT", category: "UNIDADES", severity: "INFO", fieldPath: field.fieldPath,
      originalText: m[0], suggestedText: "",
      explanation: `Valor com ponto decimal (${m[0]}). No relatório em português o separador decimal é a vírgula — confira o valor no campo de origem; a revisão não altera números.`,
      justifiable: false
    });
  }
  for (const m of field.text.matchAll(/\b\d+(?:,\d+)?\s?MM\b/g)) {
    out.push({
      ruleId: "UNIT_CASE", category: "UNIDADES", severity: "INFO", fieldPath: field.fieldPath,
      originalText: m[0], suggestedText: "",
      explanation: "Unidade em maiúsculas: o símbolo do milímetro é \"mm\".",
      justifiable: false
    });
  }
  return out;
}
